import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { featureFlags } from '@/config/featureFlags';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Upload, Target, Sparkles, History, 
  TrendingUp, ArrowRight, LogOut, User, Settings,
  Crown, BarChart3, Lock, FileText, Mail
} from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import MainMenu from '@/components/MainMenu';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const effectivePlan = user?.effective_plan || user?.plan || 'free';
  const betaAccess = user?.beta_access;
  const isBetaAccessActive = Boolean(betaAccess?.active);
  const emailVerificationPending = !user?.is_admin && !user?.email_verified;
  const formatPlanName = (plan) => `${plan?.charAt(0).toUpperCase() || ''}${plan?.slice(1) || ''}`;

  // Check if admin needs password change
  useEffect(() => {
    if (user?.requires_password_change) {
      setShowPasswordChange(true);
    }
  }, [user]);

  const handlePasswordChange = async () => {
    if (passwordData.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.changePassword(passwordData.current, passwordData.new);
      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkStatus = async (attempts = 0) => {
      if (attempts >= maxAttempts) {
        toast.info('Payment processing. Please refresh in a moment.');
        return;
      }

      try {
        const status = await api.getCheckoutStatus(sessionId);
        
        if (status.payment_status === 'paid') {
          await refreshUser();
          toast.success('Payment successful! Welcome to your new plan!');
          // Clear URL params
          window.history.replaceState({}, '', '/dashboard');
          return;
        }

        setTimeout(() => checkStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Payment status check error:', error);
      }
    };

    await checkStatus();
  }, [refreshUser]);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const plan = searchParams.get('plan');
    
    if (sessionId && plan) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const loadData = useCallback(async () => {
    try {
      const [usageData, historyData] = await Promise.all([
        api.getUsage(),
        api.getHistory(5)
      ]);
      setUsage(usageData);
      setHistory(historyData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await api.resendVerification();
      toast.success('Verification email sent. Check your inbox and spam folder.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const getPlanBadge = () => {
    const badges = {
      free: { color: 'bg-slate-100 text-slate-600', label: 'Free' },
      pro: { color: 'bg-primary/10 text-primary', label: 'Pro' },
      premium: { color: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white', label: 'Premium' }
    };
    const badge = badges[effectivePlan] || badges.free;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {isBetaAccessActive ? `Beta ${badge.label}` : badge.label}
      </span>
    );
  };

  const getUsageItem = (key) => usage?.usage?.[key];
  const hasUsageRemaining = (key) => {
    const item = getUsageItem(key);
    return !item || item.is_unlimited || item.remaining > 0;
  };
  const usageRows = [
    { key: 'analyses', label: 'Resume analyses' },
    { key: 'comparisons', label: 'Resume comparisons' },
    { key: 'resume_generations', label: 'Custom resume generations' }
  ];
  const hasPremiumAccess = user?.is_admin || effectivePlan === 'premium';
  const canUseProductFeatures = !emailVerificationPending;
  const guidedResumeCreatorEnabled = featureFlags.guidedResumeCreator;
  const guidedResumeUsage = usage?.usage?.guided_resume_creator;
  const guidedResumeActiveSession = usage?.guided_resume_active_session;
  const guidedResumeHasCredits = !guidedResumeUsage
    || guidedResumeUsage.is_unlimited
    || (guidedResumeUsage.remaining ?? 0) > 0;

  const getHistoryMeta = (type) => {
    const meta = {
      analysis: {
        label: 'Resume analysis',
        color: 'bg-primary/10 text-primary',
        icon: <BarChart3 className="w-5 h-5" />
      },
      compare: {
        label: 'Resume comparison',
        color: 'bg-accent/10 text-accent',
        icon: <Target className="w-5 h-5" />
      },
      generate: {
        label: 'Resume builder',
        color: 'bg-success/10 text-success',
        icon: <Sparkles className="w-5 h-5" />
      },
      cover_letter: {
        label: 'Cover letter',
        color: 'bg-secondary/10 text-secondary',
        icon: <FileText className="w-5 h-5" />
      }
    };

    return meta[type] || {
      label: type.replace(/_/g, ' '),
      color: 'bg-surface text-muted-foreground',
      icon: <History className="w-5 h-5" />
    };
  };

  const quickActions = [
    {
      title: "Analyze Resume",
      description: "Get a detailed score and feedback",
      icon: <BarChart3 className="w-6 h-6" />,
      path: "/analysis",
      available: canUseProductFeatures && hasUsageRemaining('analyses'),
      badge: "Monthly limit reached"
    },
    {
      title: "Compare to Job",
      description: "Match your resume to a job posting",
      icon: <Target className="w-6 h-6" />,
      path: "/compare",
      available: canUseProductFeatures && hasUsageRemaining('comparisons'),
      badge: "Monthly limit reached"
    },
    {
      title: "AI Resume Builder",
      description: "Generate an optimized resume",
      icon: <Sparkles className="w-6 h-6" />,
      path: "/builder",
      available: canUseProductFeatures && hasUsageRemaining('resume_generations'),
      badge: "Monthly limit reached"
    },
    {
      title: "Cover Letter Generator",
      description: "Draft a tailored cover letter",
      icon: <FileText className="w-6 h-6" />,
      path: "/cover-letter",
      available: canUseProductFeatures && hasPremiumAccess,
      badge: "Premium only"
    }
  ];

  if (guidedResumeCreatorEnabled) {
    quickActions.splice(3, 0, {
      title: guidedResumeActiveSession ? "Continue Guided Resume" : "Guided Resume Creator",
      description: guidedResumeActiveSession
        ? "Continue your unfinished general resume session"
        : "Build or improve a general ATS-friendly resume",
      icon: <Sparkles className="w-6 h-6" />,
      path: "/guided-resume-creator",
      available: canUseProductFeatures && (Boolean(guidedResumeActiveSession) || guidedResumeHasCredits),
      badge: guidedResumeActiveSession ? null : "No credits remaining",
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MainMenu />
            <AppLogo />
          </div>
          <div className="flex items-center gap-4">
            {effectivePlan === 'free' && !user?.is_admin && (
              <button
                onClick={() => navigate('/pricing')}
                className="hidden sm:flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors"
                data-testid="upgrade-btn"
              >
                <Crown className="w-4 h-4" />
                Upgrade
              </button>
            )}
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="hidden sm:flex items-center gap-2 bg-warning/10 text-warning px-4 py-2 rounded-lg text-sm font-medium hover:bg-warning/20 transition-colors"
                data-testid="admin-btn"
              >
                <Settings className="w-4 h-4" />
                Admin
              </button>
            )}
            <button
              onClick={() => navigate('/account')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="account-btn"
            >
              <div className="w-8 h-8 bg-surface rounded-full flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              Welcome back, {user?.name?.split(' ')[0] || 'there'}!
            </h1>
            {getPlanBadge()}
          </div>
          <p className="text-muted-foreground">
            {isBetaAccessActive
              ? `You're using beta ${formatPlanName(effectivePlan)} access${betaAccess?.expires_at ? ` through ${new Date(betaAccess.expires_at).toLocaleDateString()}` : ''}.`
              : effectivePlan === 'free'
              ? "You're on the Free plan with monthly allowances for analysis, comparison, and resume generation."
              : `You're on the ${formatPlanName(effectivePlan)} plan.`
            }
          </p>
        </div>

        {emailVerificationPending && (
          <div className="mb-8 rounded-xl border border-primary/20 bg-primary/5 p-5" data-testid="verify-email-banner">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground">Verify your email to unlock resume tools</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Resume analysis, comparisons, AI resume generation, cover letters, and exports stay locked until your email is verified.
                    Check your inbox for the verification email, or resend it below.
                  </p>
                </div>
              </div>
              <button
                onClick={handleResendVerification}
                disabled={resendingVerification}
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
                data-testid="resend-verification-btn"
              >
                {resendingVerification ? 'Sending...' : 'Resend verification email'}
              </button>
            </div>
          </div>
        )}

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Usage Card - Large */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:col-span-5 bg-white border border-border rounded-xl p-6 shadow-soft"
            data-testid="usage-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Usage This Month</h2>
              <TrendingUp className="w-5 h-5 text-muted-foreground" />
            </div>
            
            {usage && (
              <div className="space-y-3">
                {usageRows.map((row) => {
                  const item = getUsageItem(row.key);
                  if (!item) return null;
                  return (
                    <div key={row.key} className="flex items-center justify-between p-3 bg-surface rounded-lg">
                      <span className="text-sm text-muted-foreground">{row.label}</span>
                      <span className="font-medium">
                        {item.is_unlimited ? 'Unlimited' : `${item.used} / ${item.limit}`}
                      </span>
                    </div>
                  );
                })}
                {usage.reset_date && (
                  <p className="text-xs text-muted-foreground">
                    Monthly allowances reset on {new Date(usage.reset_date).toLocaleDateString()}.
                  </p>
                )}
              </div>
            )}
          </motion.div>

          {/* Quick Actions - Medium */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="md:col-span-7 bg-white border border-border rounded-xl p-6 shadow-soft"
            data-testid="quick-actions"
          >
            <h2 className="font-heading text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (emailVerificationPending) {
                      return;
                    }
                    if (action.available) {
                      navigate(action.path);
                    } else {
                      navigate('/pricing');
                    }
                  }}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                    action.available 
                      ? 'border-border hover:border-primary/30 hover:bg-surface'
                      : 'border-border/50 bg-surface/50'
                  }`}
                  data-testid={`action-${index}`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    action.available ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{action.title}</span>
                      {action.badge && !action.available && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                          {emailVerificationPending ? 'Verify email first' : action.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                  <ArrowRight className={`w-5 h-5 ${action.available ? 'text-primary' : 'text-slate-300'}`} />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="md:col-span-12 bg-white border border-border rounded-xl p-6 shadow-soft"
            data-testid="history-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold">Recent Activity</h2>
              <History className="w-5 h-5 text-muted-foreground" />
            </div>
            
            {history.length > 0 ? (
              <div className="space-y-3">
                {history.map((item, index) => {
                  const meta = getHistoryMeta(item.type);

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-surface rounded-lg"
                      data-testid={`history-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                          {meta.icon}
                        </div>
                        <div>
                          <p className="font-medium">{meta.label}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {item.result?.overall_score && (
                        <div className="text-right">
                          <p className="font-heading text-xl font-bold">{item.result.overall_score}</p>
                          <p className="text-xs text-muted-foreground">Score</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Upload className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No activity yet. Start by analyzing your resume!</p>
                <button
                  onClick={() => navigate('/analysis')}
                  className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
                  data-testid="start-analysis-btn"
                >
                  Analyze Resume
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold">Change Password Required</h3>
                <p className="text-sm text-muted-foreground">Account security requirement</p>
              </div>
            </div>
            <p className="text-muted-foreground mb-6">
              For security, please change your password before continuing.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Current Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.current}
                  onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                  className="w-full h-11 px-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Current password"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                  className="w-full h-11 px-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="New password (min 8 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                  className="w-full h-11 px-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="rounded"
                />
                Show passwords
              </label>
            </div>
            <button
              onClick={handlePasswordChange}
              disabled={changingPassword || !passwordData.current || !passwordData.new || !passwordData.confirm}
              className="w-full mt-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
